import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import SectionLoader from '../../../components/SectionLoader';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../../../assets/scss/component.scss';
import { error } from "../../../notify";

const SitePendingFST = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const ERR_NETWORK_MESSAGE = 'Server is not available.';
    const token = sessionStorage.getItem("revizeTrainingToken");
    const user_id = sessionStorage.getItem("loggedInUserId");

    // API endpoints
    const pendingFstApi = `${process.env.REACT_APP_SERVER_URL}/api/pending_fst_records`;
    const setPageLimitURL = `${process.env.REACT_APP_SERVER_URL}/api/set-page-limit/`;
    const getPageLimitApi = `${process.env.REACT_APP_SERVER_URL}/api/get_page_limit/`;

    // State management
    const [fstData, setFSTData] = useState([]);
    const [websites, setWebsites] = useState([{ name: "All", id: "All" }]);
    const [selectedWebsite, setSelectedWebsite] = useState("All");
    const [showPrevious, setShowPrevious] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentRangeStart, setCurrentRangeStart] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [showClearButton, setShowClearButton] = useState(false);

    const pagesToShow = 10;
    const offset = useRef(0);

    // Icons
    const RefreshIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a6acf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2v6h6"></path>
            <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
            <path d="M21 22v-6h-6"></path>
            <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
        </svg>
    );

    const BackIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
            <line x1="5" y1="6" x2="5" y2="18"></line>
        </svg>
    );

    const SearchIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
        </svg>
    );

    const ClearIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    // Fetch website list on mount
    useEffect(() => {
        const fetchWebsites = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_SERVER_URL}/api/analytics/website_list?user_id=${user_id}`,
                    { headers: { "Authorization": `Bearer ${token}` } }
                );
                setWebsites([{ name: "All", id: "All" }, ...response.data]);
            } catch (err) {
                console.error("Error fetching website list:", err);
                error(err.response?.data?.detail || err.message);
            }
        };

        if (token) fetchWebsites();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, user_id]);

    // Get page limit on mount
    useEffect(() => {
        const fetchPageLimit = async () => {
            try {
                const cachedPageLimit = sessionStorage.getItem('current_page_limit');
                if (cachedPageLimit) {
                    setItemsPerPage(parseInt(cachedPageLimit, 10));
                    return;
                }

                const pageLimitResponse = await axios.get(getPageLimitApi, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { user_id }
                });
                const pageLimitData = pageLimitResponse.data;
                setItemsPerPage(pageLimitData.page_limit);
                sessionStorage.setItem('current_page_limit', pageLimitData.page_limit);
            } catch (err) {
                console.error('Error fetching page limit:', err);
                error(err.response?.data?.detail || err.message);
                setItemsPerPage(100);
            }
        };

        if (token) fetchPageLimit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, user_id]);

    // Set page limit when user changes it (not on initial load)
    const updatePageLimit = async (newLimit) => {
        if (token && newLimit) {
            try {
                await axios.post(
                    setPageLimitURL,
                    { page_limit: newLimit },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { user_id }
                    }
                );
                sessionStorage.setItem('current_page_limit', newLimit);
            } catch (err) {
                console.error('Error setting page limit:', err);
                error(err.response?.data?.detail || err.message);
            }
        }
    };

    // Search functionality
    const handleSearch = async () => {
        if (searchInputValue.trim() !== "") {
            setShowClearButton(true);
        }
        setCurrentPage(1);
        setCurrentRangeStart(1);
        const currentOffset = 0;
        offset.current = currentOffset;
        await fetchData(searchInputValue);
    };

    const handleClearSearch = async () => {
        setSearchInputValue("");
        setShowClearButton(false);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        const currentOffset = 0;
        offset.current = currentOffset;
        await fetchData("");
    };

    // Refresh functionality
    const handleRefresh = async () => {
        const currentOffset = (currentPage - 1) * itemsPerPage;
        offset.current = currentOffset;
        await fetchData(searchInputValue);
    };

    // Fetch data when pagination or filters change
    const fetchData = async (searchText = "") => {
        setIsLoading(true);
        const currentOffset = (currentPage - 1) * itemsPerPage;
        offset.current = currentOffset;

        try {
            if (!itemsPerPage) {
                return;
            }
            const response = await axios.get(
                pendingFstApi,
                {
                    headers: { "Authorization": `Bearer ${token}` },
                    params: {
                        offset: currentOffset,
                        limit: itemsPerPage,
                        show_previous_records: showPrevious,
                        website: selectedWebsite === "All" ? "All" : websites.find(w => w.name === selectedWebsite)?.id,
                        search_text: searchText.trim()
                    }
                }
            );
            setFSTData(Array.isArray(response.data?.data) ? response.data.data : []);
            if (response.data?.total_pages && currentOffset === 0) {
                setTotalPages(response.data.total_pages);
            }
            setIsLoading(false);
        } catch (err) {
            console.error("Error fetching data:", err);
            setFSTData([]);
            setIsLoading(false);
            if (err.code === 'ERR_NETWORK') {
                error(ERR_NETWORK_MESSAGE);
            } else {
                error(err.response?.data?.detail || err.message);
            }
            navigate('/site');
        }
    };

    useEffect(() => {
        if (token && location.state) {
            const currentOffset = (currentPage - 1) * itemsPerPage;
            offset.current = currentOffset;
            fetchData(searchInputValue);
        } else {
            navigate('/site');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, currentPage, itemsPerPage, showPrevious, selectedWebsite, navigate, location.state]);

    // Clear search when showPrevious is toggled off
    useEffect(() => {
        if (!showPrevious && searchInputValue) {
            handleClearSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPrevious]);

    // Pagination handlers
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        if (pageNumber < currentRangeStart || pageNumber >= currentRangeStart + pagesToShow) {
            setCurrentRangeStart(Math.floor((pageNumber - 1) / pagesToShow) * pagesToShow + 1);
        }
    };


    const handleItemsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setItemsPerPage(newLimit);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        updatePageLimit(newLimit);
    };

    const handleShowPreviousChange = (e) => {
        const isChecked = e.target.checked;
        setShowPrevious(isChecked);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        
        if (!isChecked && searchInputValue) {
            setSearchInputValue("");
            setShowClearButton(false);
        }
    };

    const handleWebsiteSelect = (websiteName) => {
        setSelectedWebsite(websiteName);
        setCurrentPage(1);
        setCurrentRangeStart(1);
    };

    const openRecordModal = (record) => {
        setSelectedRecord(record);
        setShowModal(true);
    };

    const closeRecordModal = () => {
        setShowModal(false);
        setSelectedRecord(null);
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        const formattedDate = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
        });
        const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        return `${formattedDate}, ${formattedTime}`;
    };

    // Calculate visible page numbers
    const visiblePageNumbers = Array.from(
        { length: Math.min(pagesToShow, totalPages - currentRangeStart + 1) },
        (_, i) => currentRangeStart + i
    );

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <tr>
                    <td colSpan="3" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                        <SectionLoader label="Loading pending FST records..." className="revize-section-loader--table" />
                    </td>
                </tr>
            );
        }

        if (!fstData || fstData.length === 0) {
            return (
                <tr>
                    <td colSpan="3" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                        {searchInputValue ? 'No records found matching your search.' : 'No records found'}
                    </td>
                </tr>
            );
        }

        return fstData.map((item, index) => (
            <tr key={index} style={{ cursor: 'pointer' }}>
                <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0' }}>
                    <span
                        onClick={() => openRecordModal(item)}
                        style={{ cursor: 'pointer' }}
                    >
                        {item.website_name}
                    </span>
                </td>
                <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                    <span
                        onClick={() => openRecordModal(item)}
                        style={{ cursor: 'pointer' }}
                    >
                        {item.status}
                    </span>
                </td>
                <td style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                    <span
                        onClick={() => openRecordModal(item)}
                        style={{ cursor: 'pointer' }}
                    >
                        {formatTimestamp(item.requested_time)}
                    </span>
                </td>
            </tr>
        ));
    };

    return (
        <div style={{ 
            fontFamily: '"Inter", sans-serif', 
            background: '#f8f9fd', 
            margin: 0, 
            padding: '20px', 
            minHeight: '100vh',
            overflowX: 'auto', // Added horizontal scrolling for the entire container
            width: '100%'
        }}>
            <style>
                {`
                    .custom-checkbox input[type="checkbox"] {
                        accent-color: #5A6ACF;
                    }
                    .custom-checkbox .form-check-input:checked {
                        background-color: #5A6ACF;
                        border-color: #5A6ACF;
                    }
                    .custom-checkbox .form-check-input:focus {
                        border-color: #5A6ACF;
                        box-shadow: 0 0 0 0.2rem rgba(90, 106, 207, 0.25);
                    }
                `}
            </style>

            <div style={{ 
                background: '#fff', 
                borderRadius: '16px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)', 
                padding: '20px',
                minWidth: '800px' // Ensure minimum width for the content
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    marginBottom: '15px', 
                    flexWrap: 'wrap', 
                    gap: '15px',
                    position: 'relative'
                }}>
                    <h2 style={{ fontSize: '20px', margin: 0, fontWeight: 600, flex: 1 }}>
                        Pending FST Records
                    </h2>

                    {showPrevious && (
                        <div style={{ 
                            position: 'absolute', 
                            left: '50%', 
                            transform: 'translateX(-50%)',
                            width: '350px',
                            minWidth: '250px'
                        }}>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    placeholder="Search website names..."
                                    value={searchInputValue}
                                    onChange={(e) => {
                                        setSearchInputValue(e.target.value);
                                        if (e.target.value.trim() === "") {
                                            setShowClearButton(false);
                                        }
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px 68px 8px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '14px'
                                    }}
                                />
                                <div style={{
                                    position: 'absolute',
                                    right: '4px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'center'
                                }}>
                                    {showClearButton && (
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleClearSearch}
                                            style={{
                                                background: '#5a6acf',
                                                borderRadius: '8px',
                                                width: '28px',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0',
                                                cursor: 'pointer',
                                                color: '#5a6acf',
                                                transition: 'all 0.2s ease-in-out',
                                                border: '1px solid #ddd',
                                                backgroundColor: 'white',
                                                boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#5a6acf';
                                                e.currentTarget.style.color = 'white';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'white';
                                                e.currentTarget.style.color = '#5a6acf';
                                            }}
                                        >
                                            <ClearIcon />
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={handleSearch}
                                        style={{
                                            background: '#5a6acf',
                                            borderRadius: '8px',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0',
                                            cursor: 'pointer',
                                            color: '#5a6acf',
                                            transition: 'all 0.2s ease-in-out',
                                            border: '1px solid #ddd',
                                            backgroundColor: 'white',
                                            boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#5a6acf';
                                            e.currentTarget.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'white';
                                            e.currentTarget.style.color = '#5a6acf';
                                        }}
                                    >
                                        <SearchIcon />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px', 
                        flexWrap: 'wrap',
                        flex: 1,
                        justifyContent: 'flex-end'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div className="form-check custom-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="show-previous"
                                    checked={showPrevious}
                                    onChange={handleShowPreviousChange}
                                    style={{ 
                                        margin: 0,
                                        width: '18px',
                                        height: '18px',
                                        cursor: 'pointer'
                                    }}
                                />
                                <label 
                                    className="form-check-label" 
                                    htmlFor="show-previous"
                                    style={{ 
                                        fontSize: '14px', 
                                        color: '#555', 
                                        margin: 0,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Show previous records
                                </label>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <select
                                value={selectedWebsite}
                                onChange={(e) => handleWebsiteSelect(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    width: '200px',
                                    minWidth: '150px'
                                }}
                            >
                                {websites.map(website => (
                                    <option key={website.name} value={website.name}>
                                        {website.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <OverlayTrigger placement="top" overlay={<Tooltip>Refresh</Tooltip>}>
                            <Button
                                variant="outline-secondary"
                                onClick={handleRefresh}
                                style={{
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0',
                                    fontSize: '14px',
                                    color: '#5a6acf',
                                    cursor: 'pointer',
                                }}
                            >
                                <RefreshIcon />
                            </Button>
                        </OverlayTrigger>

                        <OverlayTrigger placement="top" overlay={<Tooltip>Back</Tooltip>}>
                            <Button
                                variant="outline-secondary"
                                style={{
                                    background: 'white',
                                    border: '1px solid #ddd',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0',
                                    fontSize: '14px',
                                    color: '#5a6acf',
                                    cursor: 'pointer',
                                }}
                            >
                                <Link
                                    to="/site"
                                    style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <BackIcon />
                                </Link>
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>

                {/* Table Section - Made horizontally scrollable */}
                <div style={{ overflowX: 'auto', width: '100%' }}>
                    <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        minWidth: '700px' // Ensure table doesn't get too narrow
                    }}>
                        <thead>
                            <tr style={{ textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#555' }}>
                                <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '50%' }}>Website Name</th>
                                <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '25%', textAlign: 'center' }}>Status</th>
                                <th style={{ padding: '14px 10px', borderBottom: '1px solid #f0f0f0', width: '25%', textAlign: 'center' }}>Requested Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderTableContent()}
                        </tbody>
                    </table>
                </div>

                {!isLoading && fstData.length > 0 && (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        marginTop: '15px', 
                        fontSize: '14px', 
                        color: '#888',
                        flexWrap: 'wrap',
                        gap: '10px'
                    }}>
                        <div style={{ flex: 1, textAlign: 'left', minWidth: '200px' }}>
                            <span>Showing data {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, fstData.length)} of {fstData.length} entries</span>
                        </div>

                        <div style={{ 
                            flex: 1, 
                            display: 'flex', 
                            justifyContent: 'center', 
                            gap: '6px', 
                            alignItems: 'center',
                            minWidth: '300px'
                        }}>
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '4px 8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    background: '#fff',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    opacity: currentPage === 1 ? 0.5 : 1
                                }}
                            >
                                &lt;
                            </button>

                            {visiblePageNumbers.map((number) => (
                                <button
                                    key={number}
                                    onClick={() => paginate(number)}
                                    style={{
                                        padding: '4px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: currentPage === number ? '#5A6ACF' : '#fff',
                                        color: currentPage === number ? '#fff' : 'inherit',
                                        cursor: 'pointer',
                                        fontSize: '13px'
                                    }}
                                >
                                    {number}
                                </button>
                            ))}

                            {currentRangeStart + pagesToShow <= totalPages && (
                                <span>...</span>
                            )}

                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                    padding: '4px 8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    background: '#fff',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    opacity: currentPage === totalPages ? 0.5 : 1
                                }}
                            >
                                &gt;
                            </button>
                        </div>

                        <div style={{ 
                            flex: 1, 
                            textAlign: 'right',
                            minWidth: '200px'
                        }}>
                            <label htmlFor="itemsPerPage" style={{ marginRight: '10px' }}>Items per page:</label>
                            <select
                                id="itemsPerPage"
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    height: '32px',
                                    width: '80px',
                                    fontSize: '13px',
                                    appearance: 'menulist'
                                }}
                            >
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={150}>150</option>
                                <option value={200}>200</option>
                                <option value={500}>500</option>
                                <option value={1000}>1000</option>
                            </select>
                        </div>
                    </div>
                )}

                {selectedRecord && (
                    <Modal show={showModal} onHide={closeRecordModal} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Record Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="mt-3">
                                <h5>Website:</h5>
                                <pre style={{
                                    backgroundColor: '#f4f4f4',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {selectedRecord.website_name}
                                </pre>
                            </div>
                            <div className="mt-3">
                                <h5>Status:</h5>
                                <pre style={{
                                    backgroundColor: '#f4f4f4',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {selectedRecord.status}
                                </pre>
                            </div>
                            <div className="mt-3">
                                <h5>Requested Time:</h5>
                                <pre style={{
                                    backgroundColor: '#f4f4f4',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {formatTimestamp(selectedRecord.requested_time)}
                                </pre>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={closeRecordModal}
                                style={{ border: '1px solid #ddd' }}
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}
            </div>
        </div>
    );
};

export default SitePendingFST;